import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import axiosApi from '../Utils/axiosApi';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';


import { API_BASE_URL } from '../../config';

const BookingFormDialog = ({ open, onClose, hostelId, searchDates }) => {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [rooms, setRooms] = useState([]);

  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formMessage, setFormMessage] = useState(null);
  const contentRef = useRef(null);

  const checkIn = searchDates?.checkIn;
  const checkOut = searchDates?.checkOut;

  const [bookingDetails, setBookingDetails] = useState({
    name: '',
    email: '',
    phone: '',
    room: '',
    fromDate: '',
    toDate: '',
    countryId: '',
    stateId: '',
    address: '',
    applicantGender: '',
    applicantLocation: '',
  });

  /* ================= FETCH COUNTRIES ================= */
  useEffect(() => {
    if (!open) return;

    axios
      .post(`${API_BASE_URL}/public/getCountry`, {})
      .then((res) => setCountries(res.data || []))
      .catch(() => setCountries([]));
  }, [open]);

  /* ================= FETCH STATES ================= */
  useEffect(() => {
    if (!bookingDetails.countryId) {
      setStates([]);
      return;
    }

    axios
      .post(`${API_BASE_URL}/public/getState`, {
        countryIds: [bookingDetails.countryId],
      })
      .then((res) => setStates(res.data || []))
      .catch(() => setStates([]));
  }, [bookingDetails.countryId]);

  /* ================= FETCH ROOMS ================= */
  useEffect(() => {
    if (!open || !hostelId) return;

    setLoadingRooms(true);

    axiosApi
      .post('/public/getHostelInfo', {
        hostelSearchCriteria: {
          hostelIds: [hostelId],
          searchCheckInDate: checkIn || null,
          searchCheckOutDate: checkOut || null,
        },
      })
      .then((res) => {
        const dto = res.data;

        const fetchedRooms =
          dto.hostelRoomDTOs?.map((room) => ({
            type: room.roomType,
            availabilityCount: room.availabilityCount,
            dailyPrice: room.dailyPrice,
            monthlyPrice: room.monthlyPrice,
          })) || [];

        setRooms(fetchedRooms);
      })
      .catch(() => setRooms([]))
      .finally(() => setLoadingRooms(false));
  }, [open, hostelId, checkIn, checkOut]);

  /* ================= SCROLL TO MESSAGE ================= */
  useEffect(() => {
    if (formMessage && contentRef.current) {
      contentRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [formMessage]);

  /* ================= HANDLE CHANGE ================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setBookingDetails((p) => ({ ...p, [name]: value }));
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    const {
      name,
      email,
      phone,
      room,
      fromDate,
      toDate,
      countryId,
      stateId,
    } = bookingDetails;

    if (
      !name ||
      !email ||
      !phone ||
      !room ||
      !fromDate ||
      !toDate ||
      !countryId ||
      !stateId
    ) {
      setFormMessage({
        type: 'error',
        text: 'Please fill all required fields.',
      });
      return;
    }

    const payload = {
      hostelBookingRequestInfoDTO: {
        applicantFullName: name,
        applicantEmail: email,
        applicantMobileNumber: phone,
        hostelId,
        preferredRoomType: room,
        hostelJoiningDate: fromDate,
        plannedAccommodationEndDate: toDate,
        bookingStatus: 'PENDING',
        applicantGender: bookingDetails.applicantGender,
        applicantLocation: bookingDetails.applicantLocation,
        permanentAddress: bookingDetails.address,
        countryId,
        stateId,
      },
    };

    try {
      setLoading(true);

      await axios.post(
        `${API_BASE_URL}/public/createHostelBookingRequestInfo`,
        payload
      );

      setFormMessage({
        type: 'success',
        text: 'Booking request submitted!',
      });
    } catch {
      setFormMessage({
        type: 'error',
        text: 'Booking failed. Try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
    >
      {/* HEADER */}
      
 <DialogTitle
  sx={{
    fontWeight: 700,
    pr: 7,          // ✅ space for button
    position: 'relative',
  }}
>
  🏨 Book Your Stay

  <IconButton
    onClick={onClose}   // ✅ IMPORTANT FIX
    aria-label="close"
    sx={{
      position: 'absolute',
      top: 10,
      right: 16,

      width: 40,
      height: 40,
      borderRadius: '50%',

      backgroundColor: 'rgba(0,0,0,0.04)',
      border: '1px solid rgba(0,0,0,0.08)',

      '&:hover': {
        backgroundColor: 'rgba(0,0,0,0.08)',
      },

      fontSize: '20px',
      fontWeight: 500,
      color: '#333',

      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    ×
  </IconButton>
</DialogTitle>

      {/* CONTENT */}
      <DialogContent
  dividers
  ref={contentRef}
  sx={{
    p: 3,
    display: "flex",
    flexDirection: "column",
    gap: 2, // ⭐ THIS adds spacing between ALL fields
  }}
>
        {formMessage && (
          <Alert severity={formMessage.type} sx={{ mb: 2 }}>
            {formMessage.text}
          </Alert>
        )}

        {/* BASIC INFO */}
        <TextField size="small" name="name" label="Name" fullWidth required margin="dense" value={bookingDetails.name} onChange={handleChange} />
        <TextField size="small" name="email" label="Email" fullWidth required margin="dense" value={bookingDetails.email} onChange={handleChange} />
        <TextField size="small" name="phone" label="Mobile" fullWidth required margin="dense" value={bookingDetails.phone} onChange={handleChange} />

        {/* ROOM */}
        <FormControl fullWidth required margin="dense" size="small">
          <InputLabel>Select Room Type</InputLabel>
          <Select name="room" value={bookingDetails.room} onChange={handleChange} disabled={loadingRooms}>
            {loadingRooms && <MenuItem disabled>Loading rooms...</MenuItem>}

            {!loadingRooms &&
              rooms.filter(r => r.availabilityCount > 0).map(r => (
                <MenuItem key={r.type} value={r.type}>
                  {r.type} (₹{r.monthlyPrice || r.dailyPrice})
                </MenuItem>
              ))}

            {!loadingRooms && rooms.filter(r => r.availabilityCount > 0).length === 0 && (
              <MenuItem disabled>No rooms available</MenuItem>
            )}
          </Select>
        </FormControl>

        {/* DATES */}
        <div style={{ display: 'flex', gap: 8 }}>
          <TextField
            size="small"
            type="date"
            name="fromDate"
            label="From"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={bookingDetails.fromDate}
            onChange={handleChange}
          />
          <TextField
            size="small"
            type="date"
            name="toDate"
            label="To"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={bookingDetails.toDate}
            onChange={handleChange}
          />
        </div>

        {/* COUNTRY */}
        <FormControl fullWidth required margin="dense" size="small">
          <InputLabel>Country</InputLabel>
          <Select name="countryId" value={bookingDetails.countryId} onChange={handleChange}>
            {countries.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.countryName}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* STATE */}
        <FormControl fullWidth required margin="dense" size="small" disabled={!bookingDetails.countryId}>
          <InputLabel>State</InputLabel>
          <Select name="stateId" value={bookingDetails.stateId} onChange={handleChange}>
            {states.map(s => (
              <MenuItem key={s.id} value={s.id}>{s.stateName}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* EXTRA */}
        <TextField size="small" name="applicantLocation" label="Location" fullWidth margin="dense" value={bookingDetails.applicantLocation} onChange={handleChange} />
        <TextField size="small" name="address" label="Address" fullWidth margin="dense" value={bookingDetails.address} onChange={handleChange} />

        <FormControl fullWidth margin="dense" size="small">
          <InputLabel>Gender</InputLabel>
          <Select name="applicantGender" value={bookingDetails.applicantGender} onChange={handleChange}>
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>

      {/* ACTIONS */}
      <DialogActions
  sx={{
    px: 2,
    pb: 2,
    display: "flex",
    flexDirection: { xs: "column", sm: "row" }, // 🔥 stack on mobile
    gap: 1.5,
  }}
>
  <Button
    onClick={onClose}
    variant="outlined"
    fullWidth
    sx={{
      textTransform: "none",
      py: 1.2, // bigger tap area
    }}
  >
    Cancel
  </Button>

  <Button
    variant="contained"
    onClick={handleSubmit}
    disabled={loading}
    fullWidth
    sx={{
      fontWeight: 600,
      py: 1.2,
      textTransform: "none",
    }}
  >
    {loading ? <CircularProgress size={20} color="inherit" /> : "Submit Booking"}
  </Button>
</DialogActions>
    </Dialog>
  );
};

export default BookingFormDialog;